import { useState, type ReactNode } from "react";
import { Gift, Star } from "lucide-react";
import { Alert, Banner, Button, LoadingState, Field, KeyValues, Modal, Select, Tag, useToast, type Tone } from "@/shared/rd";
import { Actions, Meta, SectionCard } from "../rd-local";
import type { SubscriptionPlanView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import {
  useStoreActivationQuoteQuery,
  useStoreTierQuery,
} from "../../services/queries/useStoreTierQuery";
import { useStoreTierMutation } from "../../services/mutations/useStoreTierMutation";
import { useSetStorePremiumMutation } from "../../services/mutations/useSetStorePremiumMutation";
import { formatDate, formatDateTime } from "@/lib/formatting/date-time";
import { describeTierError } from "./tierErrors";

type Action = "activate" | "IMMEDIATE" | "PERIOD_END" | "grant" | "revoke";

/** The success toast for each decision (past tense, names what changed). */
const TOAST_KEY: Record<Action, string> = {
  activate: "admin.tierActivatedToast",
  IMMEDIATE: "admin.tierCancelledToast",
  PERIOD_END: "admin.tierCancelAtEndToast",
  grant: "admin.tierGrantedToast",
  revoke: "admin.tierRevokedToast",
};

export function StoreTierControls({
  storeId,
  plans,
  canManage,
  locale,
}: {
  storeId: string;
  plans: SubscriptionPlanView[];
  canManage: boolean;
  locale: Locale;
}) {
  const t = useTranslations();
  const status = useStoreTierQuery(storeId);
  const premiumPlans = plans.filter(
    (plan) => plan.type === "PREMIUM" && plan.isActive,
  );
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const planId = selectedPlanId || premiumPlans[0]?.id || "";
  const quote = useStoreActivationQuoteQuery(
    storeId,
    planId,
    canManage && status.data?.tier === "FREE",
  );
  const toast = useToast();
  const mutation = useStoreTierMutation();
  const grant = useSetStorePremiumMutation();
  const [action, setAction] = useState<Action | null>(null);
  // The failure itself, not its text: the message is derived at render from
  // the refreshed quote (a wallet shortfall names the amount still missing).
  const [error, setError] = useState<unknown>(null);
  const [failedAction, setFailedAction] = useState<Action | null>(null);
  const busy = mutation.isPending || grant.isPending;
  const data = status.data;
  const money = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: quote.data?.plan.currency ?? data?.wallet.currency ?? "EGP",
    }).format(value);
  const actionLabel = (value: Action) =>
    t(
      value === "activate"
        ? keys.admin.tierActivate
        : value === "IMMEDIATE"
          ? keys.admin.tierCancelNow
          : value === "PERIOD_END"
            ? keys.admin.tierCancelAtEnd
            : value === "grant"
              ? keys.admin.tierGrant
              : keys.admin.tierRevoke,
    );
  const confirm = async () => {
    if (!action || !data || busy) return;
    setError(null);
    setFailedAction(null);
    try {
      if (action === "grant" || action === "revoke") {
        await grant.mutateAsync({
          storeId,
          isPremium: action === "grant",
          expectedRevision: data.revision,
        });
      } else if (action === "activate") {
        if (!quote.data?.canActivate || quote.isFetching) return;
        await mutation.mutateAsync({
          storeId,
          action,
          planId,
          expectedRevision: quote.data.revision,
          expectedPlanUpdatedAt: quote.data.plan.updatedAt,
        });
      } else {
        await mutation.mutateAsync({
          storeId,
          action: "cancel",
          timing: action,
          expectedRevision: data.revision,
        });
      }
      toast.show({ message: t(TOAST_KEY[action]) });
      setAction(null);
    } catch (failure) {
      setError(failure ?? new Error("Tier mutation failed"));
      setFailedAction(action);
      setAction(null);
    }
  };
  const shortfall = quote.data
    ? Math.max(0, quote.data.requiredAmount - quote.data.walletBalance)
    : 0;
  const errorMessage = error
    ? describeTierError(error, t, () => (shortfall > 0 ? money(shortfall) : null))
    : null;
  // Paid-active Premium (renewing or cancelling at period end). Granting
  // Complimentary over it would end the paid period at once with no credit;
  // until the founders decide D1 the UI does not offer it (the API still
  // allows it, behind the generic warning).
  const paidRunning = data?.tier === "PREMIUM";
  if (status.isLoading) {
    return <LoadingState title={t(keys.shell.loadingTitle, { page: t("admin.tierStatePage") })} sub={t(keys.shell.loadingSub)} rows={0} />;
  }
  if (!data || status.error) {
    return (
      <SectionCard badge="star" title={t(keys.admin.tierTitle)}>
        <Alert
          tone="error"
          action={
            <Button size="sm" onClick={() => void status.refetch()}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {t(keys.shell.errorTitle, { page: t("admin.tierStatePage") })}
        </Alert>
      </SectionCard>
    );
  }
  const tierLabel = t(
    data.tier === "COMPLIMENTARY"
      ? keys.admin.tierComplimentary
      : data.tier === "PREMIUM"
        ? keys.admin.tierPremium
        : keys.admin.tierFree,
  );
  const tierTone: Tone = data.tier === "PREMIUM" ? "blue" : data.tier === "COMPLIMENTARY" ? "purple" : "grey";
  const rows: Array<{ label: ReactNode; value: ReactNode }> = [];
  if (data.paidPeriodEndsAt) {
    rows.push({
      label: t(data.cancelAtPeriodEnd ? keys.admin.tierEndsAt : keys.admin.tierRenewsAt),
      value: formatDateTime(data.paidPeriodEndsAt, locale),
    });
  }
  const showQuote = canManage && data.tier === "FREE";
  if (showQuote && quote.data) {
    rows.push(
      {
        label: t(keys.admin.tierFee),
        value: (
          <span className="rd-num">
            {money(quote.data.plan.amount)} / {t(keys.admin.tierPeriodDays, { days: quote.data.plan.periodDays })}
          </span>
        ),
      },
      { label: t(keys.admin.storeSubscriptionsWalletBalance), value: <span className="rd-num">{money(quote.data.walletBalance)}</span> },
      { label: t(keys.admin.tierHistoricalDebt), value: <span className="rd-num">{money(quote.data.historicalDebtAmount)}</span> },
      { label: t(keys.admin.tierRequiredBalance), value: <span className="rd-num">{money(quote.data.requiredAmount)}</span> },
    );
  }
  return (
    <SectionCard
      badge="star"
      title={t(keys.admin.tierTitle)}
      note={data.tier === "FREE" ? t(keys.admin.tierFreeHint) : undefined}
      aside={<Tag tone={tierTone}>{tierLabel}</Tag>}
    >
      {errorMessage ? (
        <Banner
          tone="error"
          title={failedAction ? t("admin.tierFailedTitle", { action: actionLabel(failedAction) }) : undefined}
          actions={
            failedAction ? (
              <Button size="sm" onClick={() => setAction(failedAction)}>
                {t(keys.shell.retry)}
              </Button>
            ) : undefined
          }
        >
          {errorMessage}
        </Banner>
      ) : null}
      {showQuote ? (
        <Field label={t(keys.admin.subscriptionSelectLabel)} htmlFor={`tier-plan-${storeId}`}>
          <Select
            id={`tier-plan-${storeId}`}
            value={planId}
            onChange={(event) => setSelectedPlanId(event.target.value)}
            options={premiumPlans.map((plan) => ({
              value: plan.id,
              label: pickLocalized(locale, plan.nameAr, plan.nameEn),
            }))}
            placeholder={premiumPlans.length === 0 ? t(keys.common.noResults) : undefined}
          />
        </Field>
      ) : null}
      {rows.length ? <KeyValues rows={rows} /> : null}
      {showQuote && quote.data && !quote.data.canActivate ? (
        <Banner tone="warning">{t(keys.admin.tierInsufficient)}</Banner>
      ) : null}
      {showQuote && !quote.data && quote.error ? <Alert tone="error">{t(keys.errors.generic)}</Alert> : null}
      {canManage ? (
        <Actions>
          {data.tier === "FREE" ? (
            <Button
              variant="primary"
              size="sm"
              icon={Star}
              disabled={busy || quote.isFetching || !quote.data?.canActivate}
              onClick={() => setAction("activate")}
            >
              {t(keys.admin.tierActivate)}
            </Button>
          ) : null}
          {data.tier === "PREMIUM" && !data.cancelAtPeriodEnd ? (
            <Button size="sm" disabled={busy} onClick={() => setAction("PERIOD_END")}>
              {t(keys.admin.tierCancelAtEnd)}
            </Button>
          ) : null}
          {paidRunning ? null : (
            <Button
              size="sm"
              icon={Gift}
              disabled={busy}
              onClick={() => setAction(data.tier === "COMPLIMENTARY" ? "revoke" : "grant")}
            >
              {t(data.tier === "COMPLIMENTARY" ? keys.admin.tierRevoke : keys.admin.tierGrant)}
            </Button>
          )}
          {data.tier === "PREMIUM" ? (
            <Button variant="danger" size="sm" disabled={busy} onClick={() => setAction("IMMEDIATE")}>
              {t(keys.admin.tierCancelNow)}
            </Button>
          ) : null}
        </Actions>
      ) : null}
      {canManage && paidRunning && data.paidPeriodEndsAt ? (
        <Meta>
          {t(keys.admin.tierPaidRunningNoGrant, {
            date: formatDate(data.paidPeriodEndsAt, locale),
          })}
        </Meta>
      ) : null}
      {canManage && action ? (
        <Modal
          open={action !== null}
          onClose={() => {
            if (!busy) setAction(null);
          }}
          title={actionLabel(action)}
          actions={
            <>
              {/* "رجوع", not "إلغاء": beside "إلغاء فورًا" the dismiss and the
                  destructive action read as the same word. */}
              <Button variant="ghost" muted disabled={busy} onClick={() => setAction(null)}>
                {t(keys.common.back)}
              </Button>
              <Button
                variant={action === "IMMEDIATE" ? "danger" : "primary"}
                loading={busy}
                loadingLabel={t(action === "IMMEDIATE" || action === "PERIOD_END" ? "admin.busyCancelling" : "admin.busyConfirming")}
                disabled={action === "activate" && (!quote.data?.canActivate || quote.isFetching)}
                onClick={() => void confirm()}
              >
                {actionLabel(action)}
              </Button>
            </>
          }
        >
          {action === "activate" && quote.data ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                {t(keys.admin.tierActivateCharge, {
                  amount: money(quote.data.requiredAmount),
                  balance: money(quote.data.walletBalance - quote.data.requiredAmount),
                })}
              </p>
              {quote.data.historicalDebtAmount > 0 ? (
                <Meta>
                  {t(keys.admin.tierActivateChargeDebt, {
                    debt: money(quote.data.historicalDebtAmount),
                  })}
                </Meta>
              ) : null}
            </div>
          ) : null}
          <p className="rd-modal-text" style={{ margin: 0, textWrap: "pretty" }}>
            {t(
              action === "activate"
                ? keys.admin.tierActivateConfirm
                : action === "grant"
                  ? keys.admin.tierGrantConfirm
                  : action === "revoke"
                    ? keys.admin.tierRevokeConfirm
                    : keys.admin.tierCancelConfirm,
            )}
          </p>
        </Modal>
      ) : null}
    </SectionCard>
  );
}
